import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { PluginClientManifest } from "./types";
import { useFeatureFlags } from "./feature-flags/feature-flag-context";

interface PluginContextValue {
  /** All registered plugin manifests */
  plugins: PluginClientManifest[];
  /** Only plugins whose feature flag is enabled */
  enabledPlugins: PluginClientManifest[];
  /** Check if a specific plugin is enabled by its ID */
  isPluginEnabled: (pluginId: string) => boolean;
}

const PluginContext = createContext<PluginContextValue>({
  plugins: [],
  enabledPlugins: [],
  isPluginEnabled: () => false,
});

interface PluginProviderProps {
  /** All registered plugin manifests */
  plugins: PluginClientManifest[];
  children: ReactNode;
}

/**
 * Plugin provider that determines which plugins are enabled
 * based on feature flag state from FeatureFlagProvider.
 */
export function PluginProvider({ plugins, children }: PluginProviderProps) {
  const { isEnabled } = useFeatureFlags();

  const enabledPlugins = useMemo(
    () => plugins.filter((p) => isEnabled(p.featureFlagKey)),
    [plugins, isEnabled],
  );

  const value = useMemo<PluginContextValue>(
    () => ({
      plugins,
      enabledPlugins,
      isPluginEnabled: (pluginId: string) => {
        const plugin = plugins.find((p) => p.id === pluginId);
        return plugin ? isEnabled(plugin.featureFlagKey) : false;
      },
    }),
    [plugins, enabledPlugins, isEnabled],
  );

  return <PluginContext.Provider value={value}>{children}</PluginContext.Provider>;
}

/**
 * Hook to access the plugin context.
 * Returns all plugins, enabled plugins, and the isPluginEnabled helper.
 */
export function usePlugins(): PluginContextValue {
  return useContext(PluginContext);
}
